"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type AntilogView,
  computeLogMode,
  type LogComputation,
  type LogMode,
  type LogView,
  logLines,
} from "@/lib/tool-kit/arithmetic/log-view";
import { summarizePower } from "@/lib/tool-kit/arithmetic/power-view";
import { formatFraction } from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const LOGARITHM_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to find?",
  modeLog: "Logarithm, log_b(x)",
  modeAntilog: "Antilogarithm, b^y",
  modeSolve: "Solve b^x = y",
  baseLabel: "Base",
  baseTen: "10 (common log)",
  baseE: "e (natural log, ln)",
  baseTwo: "2 (binary log)",
  baseCustom: "Other",
  customBaseLabel: "Your base",
  customBaseHint: "A positive number other than 1, or e.",
  xLabel: "Number, x",
  xHint: "Must be positive: 1000, 0.001 or 2/3.",
  yLabel: "Exponent, y",
  yHint: "Any number: the antilogarithm is the base raised to it.",
  solveLabel: "Right-hand side, y",
  solveHint: "The positive number that b^x equals.",
  placesLabel: "Decimal places",
  results: "Results",
  logarithm: "Logarithm",
  antilogarithm: "Antilogarithm",
  solution: "Solution, x",
  lnX: "Natural log, ln(x)",
  logTen: "Common log, log₁₀(x)",
  logTwo: "Binary log, log₂(x)",
  lnBase: "ln of the base",
  copy: "Copy result",
  copied: "Copied!",
  workingHeading: "The working",
  precisionNote:
    "Values that are not exact fractions are computed in double precision, about 15 significant digits.",
  definition:
    "{name}({x}) is the exponent you must put on {b} to get {x}: {b}^? = {x}.",
  exactPower: "{x} = {b}^{kp}, so {name}({x}) = {k}.",
  zeroRule:
    "{name}(1) = 0 for every base, because any base to the power 0 is 1.",
  oneRule: "{name}({b}) = 1, because a base to the power 1 is itself.",
  changeOfBase:
    "Change of base: {name}({x}) = ln({x}) / ln({b}) = {lnX} / {lnB} = {value}.",
  solveStart: "Solve {b}^x = {y}.",
  solveTake: "Take the logarithm of both sides: x · ln({b}) = ln({y}).",
  solveIsolate:
    "Divide by ln({b}): x = ln({y}) / ln({b}) = {lnY} / {lnB} = {value}.",
  check: "Check: {b}^({value}) ≈ {back}.",
  antilogRule:
    "The antilogarithm undoes the logarithm: if {name}(x) = {y}, then x = {b}^{y}.",
  antilogResult: "{b}^({y}) = {result}",
  enterBoth: "Enter a base and a number to see the logarithm.",
  invalidBase: "The base is not a number. Try 10, 2, 1.5 or e.",
  invalidX: "That is not a number. Try 1000, 0.001 or 2/3.",
  baseNotPositive:
    "The base must be positive. A logarithm base cannot be zero or negative.",
  baseIsOne:
    "The base cannot be 1: every power of 1 is 1, so no exponent would reach any other number.",
  xNotPositive:
    "The logarithm of zero or a negative number is not defined for real numbers. The input must be positive.",
  antilogFailed:
    "That power is undefined or not a real number — try a positive base.",
  huge: "The result is too large to write out, so it is shown in scientific notation.",
} as const;

type Strings = typeof LOGARITHM_CALCULATOR_STRINGS;
type BaseChoice = "ten" | "e" | "two" | "custom";

const PLACE_CHOICES = [4, 6, 8, 10, 12];
const DIGITS_LIMIT = 1e15;

function LogResults({
  view,
  strings,
  places,
}: {
  view: LogView;
  strings: Strings;
  places: number;
}) {
  const fmt = useToolNumber();
  const f = (value: number) =>
    fmt(value, {
      minimumFractionDigits: places,
      maximumFractionDigits: places,
    });
  const { result, name, x } = view;
  const headline = result.exact
    ? exactText(result.exact, fmt, places)
    : `≈ ${f(result.value)}`;
  const label = view.mode === "solve" ? strings.solution : strings.logarithm;
  const lines = logLines(view, strings, f);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={label} value={headline} />
        <StatCard label={strings.lnX} value={f(result.lnX)} />
        <StatCard label={strings.logTen} value={f(result.log10X)} />
        <StatCard label={strings.logTwo} value={f(result.log2X)} />
        {view.base.isE ? null : (
          <StatCard label={strings.lnBase} value={f(result.lnBase)} />
        )}
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={`${name}(${x.text}) = ${headline}`}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <ul className="list-disc space-y-1 pl-6 text-foreground text-sm">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {result.exact ? null : (
        <p className="text-muted-foreground text-sm">{strings.precisionNote}</p>
      )}
    </>
  );
}

function exactText(
  exact: { num: bigint; den: bigint },
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string,
  places: number
): string {
  const fraction = formatFraction(exact);
  if (exact.den === BigInt(1)) {
    return fraction;
  }
  const decimal = Number(exact.num) / Number(exact.den);
  return `${fraction} = ${fmt(decimal, { maximumFractionDigits: places })}`;
}

function AntilogResults({
  view,
  strings,
  places,
}: {
  view: AntilogView;
  strings: Strings;
  places: number;
}) {
  const fmt = useToolNumber();
  let headline = "";
  let huge = false;
  if (view.exp !== null) {
    huge = !Number.isFinite(view.exp) || view.exp > DIGITS_LIMIT;
    headline = huge
      ? fmt(view.exp, { notation: "scientific", maximumFractionDigits: 8 })
      : `≈ ${fmt(view.exp, { maximumFractionDigits: places })}`;
  } else if (view.power) {
    const summary = summarizePower(view.power);
    headline = summary?.headline ?? "";
    huge = view.power.notes.includes("huge");
  }
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.antilogarithm} value={headline} />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={t(strings.antilogResult, {
          b: view.base.text,
          y: view.y.text,
          result: headline,
        })}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <p className="text-foreground text-sm">
        {t(strings.antilogRule, {
          name: view.name,
          y: view.y.text,
          b: view.base.text,
        })}
      </p>
      {huge ? <Notice>{strings.huge}</Notice> : null}
    </>
  );
}

function Problem({
  kind,
  strings,
}: {
  kind: Exclude<LogComputation["kind"], "log" | "antilog">;
  strings: Strings;
}) {
  switch (kind) {
    case "empty":
      return <Notice>{strings.enterBoth}</Notice>;
    case "invalid-base":
      return <ErrorNotice>{strings.invalidBase}</ErrorNotice>;
    case "invalid-x":
      return <ErrorNotice>{strings.invalidX}</ErrorNotice>;
    case "base-invalid":
      return <ErrorNotice>{strings.baseNotPositive}</ErrorNotice>;
    case "base-one":
      return <ErrorNotice>{strings.baseIsOne}</ErrorNotice>;
    case "x-nonpositive":
      return <ErrorNotice>{strings.xNotPositive}</ErrorNotice>;
    default:
      return <ErrorNotice>{strings.antilogFailed}</ErrorNotice>;
  }
}

function Outcome({
  outcome,
  strings,
  places,
}: {
  outcome: LogComputation;
  strings: Strings;
  places: number;
}) {
  if (outcome.kind === "log") {
    return <LogResults places={places} strings={strings} view={outcome.view} />;
  }
  if (outcome.kind === "antilog") {
    return (
      <AntilogResults places={places} strings={strings} view={outcome.view} />
    );
  }
  return <Problem kind={outcome.kind} strings={strings} />;
}

const BASE_TEXT: Record<Exclude<BaseChoice, "custom">, string> = {
  ten: "10",
  e: "e",
  two: "2",
};

export default function LogarithmCalculatorWidget() {
  const strings = useToolUi(LOGARITHM_CALCULATOR_STRINGS);
  const [mode, setMode] = useState<LogMode>("log");
  const [choice, setChoice] = useState<BaseChoice>("ten");
  const [custom, setCustom] = useState("5");
  const [x, setX] = useState("1000");
  const [places, setPlaces] = useState("8");
  const base = choice === "custom" ? custom : BASE_TEXT[choice];
  const outcome = useMemo(
    () => computeLogMode(mode, base, x, Number(places)),
    [mode, base, x, places]
  );
  const labels: Record<LogMode, [string, string]> = {
    log: [strings.xLabel, strings.xHint],
    antilog: [strings.yLabel, strings.yHint],
    solve: [strings.solveLabel, strings.solveHint],
  };
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="lg-mode"
        onChange={setMode}
        options={[
          { value: "log", label: strings.modeLog },
          { value: "antilog", label: strings.modeAntilog },
          { value: "solve", label: strings.modeSolve },
        ]}
        value={mode}
      />
      <PillGroup
        label={strings.baseLabel}
        name="lg-base"
        onChange={setChoice}
        options={[
          { value: "ten", label: strings.baseTen },
          { value: "e", label: strings.baseE },
          { value: "two", label: strings.baseTwo },
          { value: "custom", label: strings.baseCustom },
        ]}
        value={choice}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        {choice === "custom" ? (
          <TextField
            hint={strings.customBaseHint}
            id="lg-custom"
            label={strings.customBaseLabel}
            mono
            numeric
            onChange={setCustom}
            value={custom}
          />
        ) : null}
        <TextField
          hint={labels[mode][1]}
          id="lg-x"
          label={labels[mode][0]}
          mono
          numeric
          onChange={setX}
          value={x}
        />
        <SelectField
          id="lg-places"
          label={strings.placesLabel}
          onChange={setPlaces}
          options={PLACE_CHOICES.map((n) => ({
            value: String(n),
            label: String(n),
          }))}
          value={places}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome outcome={outcome} places={Number(places)} strings={strings} />
      </section>
    </>
  );
}
